import { notification } from 'antd';

export const useFileHandling = (
  setSelectedImage: (file: File | null) => void,
  setSelectedFile: (file: File | null) => void,
  setImagePreviewUrl: (url: string | null) => void,
  setFilePreviewInfo: (info: {name: string, size: string, type: string} | null) => void,
  setIsUploading: (uploading: boolean) => void
) => {
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const fileType = file.type;
      
      // Check if it's an image
      if (fileType.startsWith('image/')) {
        handleImageChange(event);
        return;
      }
      
      // Set uploading state for file preview
      setIsUploading(true);
      
      // Handle other file types
      const allowedTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
        'text/csv',
        'application/rtf',
        'text/plain'
      ];
      
      const allowedExtensions = ['.png', '.jpg', '.jpeg', '.webp', '.pdf', '.docx', '.csv', '.rtf', '.txt'];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      if (!allowedTypes.includes(fileType) && !allowedExtensions.includes(fileExtension)) {
        notification.error({
          message: 'Unsupported File Type',
          description: 'Please upload PNG, JPEG, WEBP, PDF, DOCX, CSV, RTF, or TXT files only.',
        });
        setIsUploading(false);
        return;
      }
      
      setSelectedFile(file);
      setFilePreviewInfo({
        name: file.name,
        size: (file.size / 1024 / 1024).toFixed(2) + ' MB',
        type: fileExtension.toUpperCase().substring(1)
      });
      
      // Reset uploading state after preview is set
      setTimeout(() => {
        setIsUploading(false);
      }, 500);
      
      // reset the file input value
      event.target.value = '';
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setIsUploading(true);
      
      // Create an image object to fix orientation
      const img = document.createElement('img') as HTMLImageElement;
      const reader = new FileReader();
      
      reader.onload = (e) => {
        img.onload = () => {
          // Create canvas to correct orientation
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Set canvas dimensions to match image
          canvas.width = img.width;
          canvas.height = img.height;
          
          // Draw image to canvas (automatically corrects orientation)
          ctx?.drawImage(img, 0, 0);
          
          // Convert canvas back to data URL
          const correctedDataUrl = canvas.toDataURL('image/jpeg');
          
          // Set the corrected image preview
          setImagePreviewUrl(correctedDataUrl);
          
          // Convert data URL back to File object for upload
          canvas.toBlob((blob) => {
            if (blob) {
              const correctedFile = new File([blob], file.name, { 
                type: 'image/jpeg', 
                lastModified: new Date().getTime() 
              });
              setSelectedImage(correctedFile);
            }
            setIsUploading(false);
          }, 'image/jpeg');
        };
        
        if (e.target?.result) {
          img.src = e.target.result as string;
        }
      };
      
      reader.readAsDataURL(file);

      // reset the file input value to allow re-adding the same image
      event.target.value = '';
    }
  };
  
  const removeImage = () => {
    setSelectedImage(null);
    setImagePreviewUrl(null);
    // Reset the file input value to allow re-adding the same image
    const fileInput = document.getElementById('file-upload') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setFilePreviewInfo(null);
    // Reset the file input value
    const fileInput = document.getElementById('file-upload') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  return {
    handleFileChange,
    handleImageChange,
    removeImage,
    removeFile,
  };
};
